import { MouseEvent, useRef, useEffect, ReactElement } from "react";
import { darken } from "polished";
import { last, propOr } from "ramda";
import { useSuspenseInfiniteQuery } from "@tanstack/react-query";
import { t } from "@lingui/core/macro";
import { Axis } from "@visx/axis";
import { localPoint } from "@visx/event";
import { scaleTime } from "@visx/scale";
import { useParentSize } from "@visx/responsive";
import { useTooltip, TooltipWithBounds } from "@visx/tooltip";

import { palette } from "_/style";
import { JsonValue } from "_/types";
import { PageResult, api } from "_/utils";

import { DateTime } from "_/components/date-time";
import { ColumnDef, Table } from "_/components/table";
import { Loader } from "_/components/loader";
import { Badge } from "_/components/badge";
import { JsonTree } from "_/components/json-tree";

import * as S from "./styled";

type Job = {
  id: string;
  status: string;
  task: string;
  createdAt: string;
  startedAt?: string;
  endedAt?: string;
  progress?: number;
  output: JsonValue;
};

/**
 * Query hook for retrieving all jobs.
 *
 * The result set is paginated and the next page can be fetched using
 * the `fetchNextPage` function returned in the hook object.
 *
 * See `useInfiniteQuery` docs for more details.
 */
function useAllJobs() {
  return useSuspenseInfiniteQuery({
    queryKey: ["jobs", "all"],
    initialPageParam: new Date().toISOString(),
    queryFn: async ({ pageParam }): Promise<Array<Job>> => {
      const pageParams = new URLSearchParams({
        limit: "100",
        before: pageParam,
      }).toString();

      const response = await api.get<PageResult<Job>>(`jobs?${pageParams}`);

      return response.data.data.items;
    },
    getNextPageParam: (lastPage) => last(lastPage)?.createdAt,
    refetchOnWindowFocus: false,
  });
}

export const JobsView = (): ReactElement => {
  const { data, fetchNextPage, isFetchingNextPage, hasNextPage } = useAllJobs();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Observer used for infinite scrolling.
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
          void fetchNextPage();
        }
      },
      { rootMargin: "0px", threshold: 1 }
    );

    if (!ref.current) {
      return undefined;
    }

    observer.observe(ref.current);

    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const columns: ColumnDef<Job>[] = [
    {
      accessorKey: "status",
      header: "Status",
      cell: (cell) => {
        const statusMap = {
          completed: { kind: "success", label: t`common.complete` },
          queued: { kind: "info", label: t`common.queued` },
          pending: { kind: "active", label: t`common.pending` },
          started: { kind: "highlight", label: t`common.running` },
          cancelled: { kind: "muted", label: t`common.cancelled` },
          failed: { kind: "error", label: t`common.failed` },
          unknown: { kind: "muted", label: t`common.unknown` },
        } as const;

        type Status = keyof typeof statusMap;

        const status = cell.getValue<Status>();
        const mapEntry = statusMap[status] ?? statusMap.unknown;
        const { kind, label } = mapEntry;

        return <Badge kind={kind}>{label}</Badge>;
      },
    },
    {
      accessorKey: "task",
      header: "Task",
      cell: (cell) => {
        return (
          <span style={{ fontFamily: "monospace" }}>
            {cell.getValue<string>()}
          </span>
        );
      },
    },
    {
      accessorKey: "startedAt",
      header: "Started",
      cell: ({ row: { original: rowData } }) => (
        <DateTime value={rowData.startedAt} />
      ),
    },
    {
      header: "Duration",
      cell: ({ row: { original: rowData } }) => {
        if (!rowData.startedAt || !rowData.endedAt) {
          return "--";
        }

        const start = new Date(rowData.startedAt).getTime();
        const end = new Date(rowData.endedAt).getTime();

        return `${end - start}ms`;
      },
    },
    {
      accessorKey: "output",
      header: "Output",
      cell: (cell) => {
        const dataInner = cell.getValue<JsonValue>();
        return <JsonTree data={dataInner} />;
      },
    },
  ];

  const jobs = data.pages.flat();

  return (
    <S.TabPage>
      <JobTimeline data={jobs} />
      <Table columns={columns} data={jobs} />
      <S.LoadingContainer ref={ref}>
        <Loader loading={isFetchingNextPage} />
      </S.LoadingContainer>
    </S.TabPage>
  );
};

const JobTimeline = ({ data }: { data: Job[] }): ReactElement => {
  const { parentRef, width, height } = useParentSize();
  const padding = 20;

  const colormap = {
    completed: palette.mintGreen,
    queued: palette.blizzardTeal,
    pending: palette.paleBlue,
    started: palette.lightPurple,
    cancelled: palette.aluminumGrey,
    failed: palette.blushRed,
    unknown: palette.aluminumGrey,
  } as const;

  const xScale = scaleTime({
    domain:
      data.length > 0
        ? [
            new Date(data[0].createdAt).getTime(),
            new Date(data[data.length - 1].createdAt).getTime(),
          ]
        : undefined,
    range: [padding, width - padding],
    round: true,
  });

  const {
    hideTooltip,
    showTooltip,
    tooltipData,
    tooltipLeft,
    tooltipOpen,
    tooltipTop,
  } = useTooltip<Job>();

  const handleMouseOver = (event: MouseEvent<SVGCircleElement>, job: Job) => {
    const coords = localPoint(event);

    if (!coords) {
      return;
    }

    showTooltip({
      tooltipLeft: coords.x,
      tooltipTop: coords.y,
      tooltipData: job,
    });
  };

  // A set of seen x-coordinates stored so we can offset overlapping
  // circles when generating the points and their coordinate positions.
  const seenXCoords = new Set<number>();

  const points = data.map((job) => {
    const radius = 7;
    const x = xScale(new Date(job.createdAt).getTime());
    const y = seenXCoords.has(x) ? padding - radius : padding;

    seenXCoords.add(x);

    const fill: string = propOr(palette.aluminumGrey, job.status, colormap);
    const stroke = darken(0.2, fill);

    return (
      <circle
        key={job.id}
        cx={x}
        cy={y}
        r={radius}
        fill={fill}
        stroke={stroke}
        onMouseMove={(e) => handleMouseOver(e, job)}
        onMouseLeave={hideTooltip}
      />
    );
  });

  const tooltip = tooltipOpen && tooltipData && (
    <TooltipWithBounds top={tooltipTop} left={tooltipLeft}>
      <div>
        <strong>Job ID:</strong> {tooltipData.id}
      </div>
      <div>
        <strong>Status:</strong> {tooltipData.status}
      </div>
      <div>
        <strong>Task:</strong> {tooltipData.task}
      </div>
      <div>
        <strong>Created At:</strong> <DateTime value={tooltipData.createdAt} />
      </div>
    </TooltipWithBounds>
  );

  return (
    <div ref={parentRef} style={{ width: "100%", height: "100px" }}>
      <svg width={width} height={height}>
        <Axis
          scale={xScale}
          numTicks={10}
          tickLength={24}
          top={padding}
          stroke={palette.aluminumGrey}
          tickLabelProps={{ fill: palette.aluminumGrey, fontSize: 10 }}
          tickLineProps={{ stroke: palette.aluminumGrey }}
        />
        {points}
      </svg>
      {tooltip}
    </div>
  );
};
